<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useGlobalStore } from '@stores/global'
import Input from '@components/forms/Input.vue'
import TransitionExpand from '@components/global/transitions/TransitionExpand.vue'

const emit = defineEmits(['update:modelValue'])
const props = defineProps(['modelValue', 'label', 'required', 'disabled', 'error', 'processing', 'icon', 'placeholder', 'options'])

const global = useGlobalStore()
const select = ref(null)
const root = ref(null)
const dropdown = ref(null)
const expanded = ref(false)
const selected = ref([])
const search = ref('')

// Global z-index management for proper stacking
let selectInstanceId = Math.random().toString(36).substr(2, 9)
const baseZIndex = 1000 // Base z-index for all select components (above EventCard z-10)

// Global z-index counter for proper dropdown layering
if (!window.selectZIndexCounter) {
	window.selectZIndexCounter = baseZIndex
}

// Check if mobile menu is open to adjust z-index
const isMobileMenuOpen = computed(() => global.mobile)

// Generate unique z-index for this select instance
const selectZIndex = computed(() => baseZIndex + (selectInstanceId.charCodeAt(0) % 100))

// Ensure dropdown appears above other elements when expanded, but respects mobile menu
const expandedZIndex = computed(() => {
	if (isMobileMenuOpen.value) {
		// When mobile menu is open, select components should be below it
		return baseZIndex - 100
	}
	return expanded.value ? window.selectZIndexCounter + 1 : selectZIndex.value
})

const dropdownZIndex = computed(() => {
	if (isMobileMenuOpen.value) {
		return baseZIndex - 100
	}
	return window.selectZIndexCounter + 1
})

const selectedOptions = computed(() =>
	props.options
		? props.options.filter(function (option) {
				return selected.value.includes(option.slug)
			})
		: [],
)
const filteredUnselectedOptions = computed(() =>
	props.options
		? props.options.filter(function (option) {
				return !selected.value.includes(option.slug)
			})
		: [],
)

const filteredOptions = computed(() =>
	props.options
		? props.options.filter(function (option) {
				return option.name.toLowerCase().includes(search.value.toLowerCase())
			})
		: [],
)

function toggleExpanded() {
	if (!expanded.value) {
		// When opening, increment the global counter to ensure this dropdown appears on top
		window.selectZIndexCounter += 10
	}
	expanded.value = !expanded.value
}
async function collapse(e) {
	if (!root.value.contains(e.target)) {
		expanded.value = false
	}
}
function toggleSelected(optionSlug) {
	if (selected.value.includes(optionSlug)) {
		selected.value = selected.value.filter(function (s) {
			return s !== optionSlug
		})
	} else {
		selected.value.push(optionSlug)
	}
	emit('update:modelValue', selected.value)
}
function isSelected(optionSlug) {
	return selected.value.includes(optionSlug)
}

// Watch for changes to modelValue and update selected accordingly
watch(
	() => props.modelValue,
	(newValue) => {
		selected.value = newValue ?? []
	},
	{ immediate: true },
)

onMounted(() => {
	window.addEventListener('click', collapse)
})
onBeforeUnmount(() => {
	window.removeEventListener('click', collapse)
})
</script>

<template>
	<div class="relative w-full select-none" :style="{ zIndex: expandedZIndex }" ref="root" role="listbox" aria-multiselectable="true" :aria-expanded="expanded" v-if="options">
		<button :class="['input flex w-full grow items-center justify-between rounded-lg border border-transparent bg-snk-light-500 px-4 py-3 text-sm normal-case focus:border-black/15 focus:outline-none']" @click="toggleExpanded" tabindex="0">
			<div class="flex items-center gap-2 text-nowrap" v-if="placeholder">
				{{ placeholder }}
				<div :class="['mr-2 flex h-4 w-4 items-center justify-center rounded-full bg-black/5 text-xs transition-opacity duration-200', { 'opacity-0': !selectedOptions.length }]">{{ selectedOptions.length }}</div>
			</div>
			<i :class="['fa-solid fa-chevron-down text-black/40 transition-transform duration-200', { 'rotate-180': expanded }]"></i>
		</button>
		<TransitionExpand>
			<div class="absolute left-0 top-full w-fit min-w-full pt-2" :style="{ zIndex: dropdownZIndex }" v-if="expanded" ref="dropdown">
				<div class="dropdown flex max-h-60 flex-col overflow-y-auto rounded-lg border border-black/15 bg-snk-light-500 p-1" style="overflow-y: auto">
					<button v-for="(option, i) in filteredOptions" :key="'unselected' + i" class="group flex select-none items-center gap-2 rounded-sm p-2 text-sm transition-colors duration-200 hover:bg-black/5" role="option" @click="toggleSelected(option.slug)" :aria-selected="isSelected(option.slug)" tabindex="0">
						<div class="flex h-5 w-5 items-center justify-center rounded-sm border-2 border-black/15 bg-white">
							<i class="fas fa-check text-black/60" :class="{ 'opacity-0': !isSelected(option.slug), 'opacity-100': isSelected(option.slug) }" style="transition: opacity 0.1s ease-in-out"></i>
						</div>
						<div :class="['whitespace-nowrap text-black/80 transition-colors duration-200', { 'text-black': isSelected(option.slug) }, { 'group-hover:text-black': !isSelected(option.slug) }]">{{ option.name }}</div>
					</button>
				</div>
			</div>
		</TransitionExpand>
	</div>
	<div class="flex w-fit flex-col gap-2 normal-case" v-else>
		<label class="text-xs font-medium" v-if="label">
			{{ label }}
		</label>
		<div class="relative flex w-full">
			<i :class="'absolute left-4 top-1/2 -translate-y-1/2 text-sm text-neutral-500 ' + icon" v-if="icon"></i>
			<select :class="[icon ? 'pl-10!' : 'pl-4!', { 'border-red-500!': error }]" :value="modelValue" @input="$emit('update:modelValue', $event.target.value)" :required="required" :disabled="disabled" ref="select">
				<option value="" v-if="placeholder">{{ placeholder }}</option>
				<slot></slot>
			</select>
			<i class="chevron fa-solid fa-chevron-down pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-black/40"></i>
		</div>
		<p class="text-xs text-red-500" v-if="error">
			{{ error }}
		</p>
	</div>
</template>

<style scoped>
@reference '../../../css/theme.css';

select {
	@apply w-full grow appearance-none rounded-lg border border-transparent bg-snk-light-500 px-4 py-3 text-sm normal-case transition-all duration-300 placeholder:normal-case placeholder:text-black/40 focus:border-black/15 focus:outline-none;
}
select :deep(option) {
	@apply bg-white font-poppins text-black;
}
.secondary select,
.secondary .input {
	@apply border border-black/15 focus:border-black/50;
}
.secondary .dropdown {
	@apply bg-white;
}
.primary select {
	@apply border-snk-blue-500 bg-snk-blue-500 pr-10 font-poppins font-medium text-white;
}
.primary .chevron {
	@apply ml-24 text-white;
}

/* select option {
	color: white !important;
	background-color: black !important;
}
select option:hover {
	color: white !important;
	background-color: rgb(23, 23, 23) !important;
} */
</style>
